import React, { useCallback, useRef } from 'react';
import { StyleSheet, TouchableOpacity, View } from 'react-native';
import Animated, { FadeIn, FadeOut } from 'react-native-reanimated';

import { theme } from '@garnish/constants';

import { useHovered } from '../../../../hooks';
import { webOnlyStyles } from '../../../../utils';
import { Icon } from '../../../Icon';
import { BodyText } from '../../../Text';

// ─────────────────────────────────────────────────────────────────────────────

export const IngredientCardPortionStepper = (props: Props) => {
  const {
    options,
    currentPortions,
    accessibilityLabel,
    removeButtonAccessibilityLabel,
    onSelectPortion,
    onRemove,
  } = props;

  return (
    <Animated.View
      entering={FadeIn.duration(theme.transitions.short)}
      exiting={FadeOut.duration(theme.transitions.short)}
      style={styles.outerContainer}
      pointerEvents="box-none"
    >
      <View
        style={styles.container}
        accessibilityRole="menu"
        accessibilityLabel={accessibilityLabel}
      >
        {options.map((option) => (
          <PortionOption
            key={option.portions}
            option={option}
            isSelected={currentPortions === option.portions}
            onPress={onSelectPortion}
          />
        ))}

        {currentPortions > 0 ? (
          <TouchableOpacity
            style={styles.removeButton}
            onPress={onRemove}
            accessibilityLabel={removeButtonAccessibilityLabel}
            accessibilityRole="button"
            activeOpacity={0.7}
          >
            <Icon name="IconTrash" color={theme.colors.DARK_KALE} />
          </TouchableOpacity>
        ) : null}
      </View>
    </Animated.View>
  );
};

// ─── Subcomponents ──────────────────────────────────────────────────────────

const PortionOption = (params: PortionOptionParams) => {
  const { option, isSelected, onPress } = params;

  // ─── Refs ────────────────────────────────────────────────────────────

  const pressableRef = useRef<View>(null);
  const isHovered = useHovered(pressableRef);

  // ─── Handlers ────────────────────────────────────────────────────────

  const handlePress = useCallback(() => {
    onPress(option.portions);
  }, [option.portions, onPress]);

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerStyles = [optionContainerWebStyles, isHovered ? styles.optionContainerHover : undefined]; // prettier-ignore
  const buttonStyle = [styles.optionButton, isSelected ? styles.optionButtonSelected : undefined];
  const labelStyle = [styles.optionLabel, isSelected ? styles.optionLabelSelected : undefined];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <TouchableOpacity
        ref={pressableRef}
        style={buttonStyle}
        onPress={handlePress}
        accessibilityLabel={option.label}
        accessibilityRole="menuitem"
        aria-selected={isSelected}
        activeOpacity={0.7}
      >
        <BodyText sizeMatch={['14']} style={labelStyle}>
          {option.label}
        </BodyText>

        {option.priceLabel ? (
          <BodyText sizeMatch={['14']} style={labelStyle}>
            {option.priceLabel}
          </BodyText>
        ) : null}
      </TouchableOpacity>
    </View>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  outerContainer: {
    ...StyleSheet.absoluteFillObject,
    zIndex: 1,
    alignItems: 'center',
    justifyContent: 'center',
  },
  container: {
    alignItems: 'center',
    backgroundColor: theme.colors.CREAM,
    borderRadius: 26,
    gap: theme.spacing['2'],
    boxShadow: '0px 2px 30px 0px rgba(14, 21, 14, 0.24)',
    paddingTop: theme.spacing['1'],
    paddingHorizontal: theme.spacing['1'],
    paddingBottom: theme.spacing['2'],
  },
  optionContainerHover: {
    opacity: 0.8,
  },
  optionButton: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: theme.spacing['2'],
    paddingHorizontal: theme.spacing['3'],
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.KALE.LIGHTEST,
    minWidth: 80,
    minHeight: 48,
  },
  optionButtonSelected: {
    backgroundColor: theme.colors.SPINACH,
  },
  optionLabel: {
    color: theme.colors.SPINACH,
  },
  optionLabelSelected: {
    color: theme.colors.WHITE,
  },
  removeButton: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingHorizontal: theme.spacing['4'],
    borderRadius: theme.radius.xlarge,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
});

const optionContainerWebStyles = webOnlyStyles({
  transition: `opacity ${theme.transitions.short}ms`,
});

// ─── Types ──────────────────────────────────────────────────────────────────

type Props = {
  options: PortionOptionConfig[];
  currentPortions: number;
  accessibilityLabel?: string;
  removeButtonAccessibilityLabel?: string;
  onSelectPortion: (portions: number) => void;
  onRemove: () => void;
};

type PortionOptionConfig = {
  label: string;
  portions: number;
  priceLabel?: string;
};

type PortionOptionParams = {
  option: PortionOptionConfig;
  isSelected: boolean;
  onPress: (portions: number) => void;
};

export type { PortionOptionConfig };
